import { useEffect, useRef, useState } from 'react';
import { GameClient } from '../../app/gameClient';
import { audio } from '../../engine/audio/audio';
import { Simulation } from '../../engine/simulation/simulation';
import { CIVS } from '../../gameplay/civilisations';
import { LocalTransport, ReplayTransport } from '../../networking/transport';
import { autosaveReplay } from '../../replay/replay';
import { useApp } from '../store';
import { benchConfig, setupBench, setupShowcase, showcaseConfig } from '../../app/scenarios';
import { Hud } from './Hud';
import { trackMatchEnd, trackMatchStart, type MatchMode } from '../../app/stats';

export function GameScreen() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const st = useApp.getState();
    const canvas = canvasRef.current!;
    let client: GameClient;
    let mode: MatchMode | null = null;
    try {
      const launch = st.launch;
      const g = st.prefs.graphics;
      const qs = new URLSearchParams(location.search);
      const bench = Number(qs.get('bench') ?? 0);
      const showcase = qs.get('showcase');
      if (showcase) {
        const sim = new Simulation(showcaseConfig(showcase));
        setupShowcase(sim, Number(qs.get('age') ?? 2));
        client = new GameClient(canvas, sim, new LocalTransport(0), g);
        client.cam.jumpTo(sim.map.size / 2, sim.map.size / 2 + 2, true);
        client.cam.zoom(600);
      } else if (bench > 0) {
        const sim = new Simulation(benchConfig());
        setupBench(sim, bench);
        client = new GameClient(canvas, sim, new ReplayTransport(), g);
        client.cam.jumpTo(sim.map.size / 2, sim.map.size / 2 + 4, true);
      } else if (launch?.replay && st.pendingConfig) {
        client = new GameClient(canvas, new Simulation(st.pendingConfig), new ReplayTransport(), g);
        client.setReplay(launch.replay, launch.replayEnd);
        mode = 'replay';
      } else if (launch?.sim) {
        client = new GameClient(canvas, launch.sim, launch.transport ?? new LocalTransport(0), g);
        mode = launch.transport?.networked ? 'multi' : 'load';
      } else if (st.pendingConfig) {
        const transport = launch?.transport ?? (launch?.spectate ? new ReplayTransport() : new LocalTransport(0));
        client = new GameClient(canvas, st.pendingConfig, transport, g);
        mode = transport.networked ? 'multi' : launch?.spectate ? 'spectate' : st.entry;
      } else {
        st.setScreen('menu');
        return;
      }
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
      return;
    }
    client.audio = audio;
    const gameSecs = () => client.sim.tick / 20;
    client.onGameOver = (won) => {
      audio.victory(won);
      void autosaveReplay(client.sim);
      trackMatchEnd(won ? 'win' : 'lose', gameSecs());
    };
    st.setClient(client);
    client.start();
    if (mode) {
      const cfg = client.sim.config;
      const humans = cfg.players.filter((p) => !p.ai).length;
      trackMatchStart(
        {
          mode,
          role: mode === 'multi' ? (client.local === 0 ? 'host' : 'guest') : undefined,
          players: cfg.players.length,
          humans,
          ais: cfg.players.length - humans,
          map: cfg.mapType,
          size: cfg.mapSize,
        },
        gameSecs,
      );
    }
    setReady(true);
    const civ = client.local >= 0 ? CIVS[client.sim.world.players[client.local].civ].key : 'english';
    audio.unlock();
    audio.startMusic(civ);
    return () => {
      audio.stopMusic();
      trackMatchEnd('quit', gameSecs());
      if (!client.sim.world.gameOver && client.sim.tick > 200 && client.local >= 0) void autosaveReplay(client.sim);
      client.stop();
      useApp.setState({ client: null, hud: null, launch: null });
    };
  }, []);
  return (
    <div className="game-root">
      <canvas ref={canvasRef} className="game-canvas" tabIndex={0} />
      {!ready && !error && <div className="loading">Preparing the realm…</div>}
      {error && (
        <div className="overlay"><div className="menu-card"><h2>Unable to start</h2><p>{error}</p>
          <p className="muted small">WebGL2 is required. Check that hardware acceleration is enabled in your browser.</p>
          <button className="btn" onClick={() => useApp.getState().setScreen('menu')}>Main menu</button></div></div>
      )}
      {ready && <Hud />}
    </div>
  );
}
